import { logError } from '../lib/logger';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { processPostCallRecap, getRecaps, createPostHuddleAction, getTeamMembers, exportRecap } from '../lib/api';
import { Sparkle, Download, CaretDown, CaretUp, CheckCircle, Clock, UserSwitch, X, Check, NotePencil, Eye, Plus, ListChecks, PlayCircle, Users } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { StatusChip, PageHeader, StatCard, TONE } from './enterprise/ui';

const HUDDLE_STORAGE_KEY = 'huddle_session_v1';
const OUTCOMES = [
  { key: 'assigned', label: 'Assigned', tone: 'info' },
  { key: 'resolved', label: 'Resolved', tone: 'good' },
  { key: 'carry', label: 'Carry Forward', tone: 'high' },
  { key: 'skip', label: 'Skipped', tone: 'neutral' },
];

export default function PostCallRecap() {
  const [transcript, setTranscript] = useState('');
  const [members, setMembers] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [currentRecap, setCurrentRecap] = useState(null);
  const [recaps, setRecaps] = useState([]);
  const [expandedRecap, setExpandedRecap] = useState(null);
  const [showSource, setShowSource] = useState({});
  const [loading, setLoading] = useState(true);
  const [itemStates, setItemStates] = useState({});
  const [assigningIdx, setAssigningIdx] = useState(null);
  const [assignTo, setAssignTo] = useState('');
  const [huddleSession, setHuddleSession] = useState(null);
  const [showManualInput, setShowManualInput] = useState(false);

  useEffect(() => {
    try {
      const s = localStorage.getItem(HUDDLE_STORAGE_KEY);
      if (s) setHuddleSession(JSON.parse(s));
    } catch (e) { /* noop */ }
  }, []);

  const huddleStats = useMemo(() => {
    if (!huddleSession?.decisions) return null;
    const decisions = Object.values(huddleSession.decisions);
    return {
      total: decisions.length,
      assigned: decisions.filter(d => d.outcome === 'assigned').length,
      resolved: decisions.filter(d => d.outcome === 'resolved').length,
      carry: decisions.filter(d => d.outcome === 'carry').length,
      escalations: decisions.filter(d => d.category === 'escalation').length,
      decisions,
    };
  }, [huddleSession]);

  const clearHuddleSession = () => {
    try { localStorage.removeItem(HUDDLE_STORAGE_KEY); } catch(e) { /* noop */ }
    setHuddleSession(null); toast.success('Huddle session cleared');
  };

  const loadRecaps = useCallback(async () => {
    try { setRecaps(await getRecaps()); }
    catch (e) { logError('Recaps load error:', e); }
    finally { setLoading(false); }
  }, []);

  const loadMembers = useCallback(async () => {
    try { setMembers(await getTeamMembers()); }
    catch (e) { logError('Members load error:', e); }
  }, []);

  useEffect(() => { loadRecaps(); loadMembers(); const iv = setInterval(loadRecaps, 5000); return () => clearInterval(iv); }, [loadRecaps, loadMembers]);

  // Check if today already has a recap
  const today = new Date().toISOString().split('T')[0];
  const pastRecaps = useMemo(() => recaps.filter(r => r.date !== today), [recaps, today]);
  const todayRecap = recaps.find(r => r.date === today);

  const handleProcess = async () => {
    if (!transcript.trim()) { toast.error('Paste your meeting notes first'); return; }
    setProcessing(true);
    try {
      const recap = await processPostCallRecap(transcript);
      setCurrentRecap(recap);
      toast.success(todayRecap ? 'Recap updated with new notes' : 'Recap generated and saved');
      loadRecaps();
      // Don't clear — let user see what was captured
    } catch (e) { toast.error('Failed to process'); }
    finally { setProcessing(false); }
  };

  const handleCreateAction = async (item, recapId) => {
    try {
      await createPostHuddleAction({ borrower: item.borrower || 'General', action: item.action_item || item.action || '', assigned_to: item.assigned_to || '', deadline: item.deadline || 'End of day', recap_id: recapId });
      toast.success('Action item created');
    } catch (e) { toast.error('Failed'); }
  };

  const handleAssignRecapItem = async (item, recapId, idx) => {
    if (!assignTo) { toast.error('Select member'); return; }
    try {
      await createPostHuddleAction({ borrower: item.borrower || 'General', action: item.action_item || item.action || '', assigned_to: assignTo, deadline: item.deadline || 'End of day', recap_id: recapId });
      toast.success(`Assigned to ${assignTo}`);
      setItemStates(p => ({ ...p, [`${recapId}-${idx}`]: 'assigned' }));
      setAssigningIdx(null); setAssignTo('');
    } catch (e) { toast.error('Failed'); }
  };

  const handleCompleteRecapItem = async (item, recapId, idx) => {
    try {
      await createPostHuddleAction({ borrower: item.borrower || 'General', action: item.action_item || item.action || '', assigned_to: item.assigned_to || '', deadline: 'Completed', recap_id: recapId });
      setItemStates(p => ({ ...p, [`${recapId}-${idx}`]: 'completed' }));
      toast.success('Marked complete');
    } catch (e) { toast.error('Failed'); }
  };

  const handleDismissRecapItem = (recapId, idx) => {
    setItemStates(p => ({ ...p, [`${recapId}-${idx}`]: 'dismissed' }));
  };

  const toggleSource = (id) => setShowSource(p => ({ ...p, [id]: !p[id] }));

  const renderRecapContent = (recap) => (
    <div className="space-y-4">
      {/* Summary */}
      <div className="p-3 rounded-md bg-muted/50 border border-border">
        <p className="text-sm text-foreground leading-relaxed">{recap.recap_summary}</p>
      </div>

      {/* Source Notes Toggle */}
      {recap.transcript && (
        <button onClick={() => toggleSource(recap.id)}
          className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
          data-testid={`toggle-source-${recap.id}`}>
          {showSource[recap.id] ? <CaretUp size={12} /> : <Eye size={12} />}
          {showSource[recap.id] ? 'Hide' : 'View'} Original Meeting Notes
        </button>
      )}
      {showSource[recap.id] && recap.transcript && (
        <div className="p-3 rounded-md bg-stone-50 dark:bg-stone-900/30 border border-stone-200 dark:border-stone-800 animate-fade-up" data-testid={`source-notes-${recap.id}`}>
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-2 flex items-center gap-1">
            <NotePencil size={12} /> What Was Captured
          </h4>
          <pre className="text-xs text-foreground whitespace-pre-wrap font-['IBM_Plex_Sans'] leading-relaxed">{recap.transcript}</pre>
        </div>
      )}

      {/* Items Discussed */}
      {recap.items_discussed?.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
            Items Discussed ({recap.items_discussed.length})
          </h4>
          <div className="space-y-2">
            {recap.items_discussed.map((item, i) => {
              const key = `${recap.id}-${i}`;
              const state = itemStates[key];
              if (state === 'dismissed') return null;
              const isAssigning = assigningIdx === key;
              return (
                <div key={`${recap.id}-item-${i}`} className={`rounded-md border transition-all ${state === 'completed' || state === 'assigned' ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-900/10 opacity-70' : 'border-border bg-card hover:bg-muted/30'}`} data-testid={`recap-item-${i}`}>
                  <div className="p-3 pb-1.5">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-sm font-bold text-foreground">{item.borrower || 'General'}</span>
                      {item.assigned_to && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 font-medium">{item.assigned_to}</span>}
                      {item.deadline && <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Clock size={10} />{item.deadline}</span>}
                      {item.status_update && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 font-medium">Status: {item.status_update}</span>}
                    </div>
                    {item.discussion && <p className="text-xs text-muted-foreground mb-1">{item.discussion}</p>}
                    {item.action_item && (
                      <p className={`text-xs font-medium flex items-center gap-1 ${state === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                        <CheckCircle size={12} className="text-emerald-500 flex-shrink-0" /> {item.action_item}
                      </p>
                    )}
                  </div>
                  {state === 'completed' || state === 'assigned' ? (
                    <div className="px-3 pb-2">
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                        <CheckCircle size={12} weight="fill" /> {state === 'assigned' ? 'Assigned & tracked' : 'Completed & tracked'}
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1 px-3 pb-2 pt-1 border-t border-border/50">
                        <button onClick={() => { setAssigningIdx(isAssigning ? null : key); setAssignTo(''); }}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors ${isAssigning ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' : 'text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20'}`}
                          data-testid={`assign-recap-${i}`}>
                          <UserSwitch size={12} /> Assign
                        </button>
                        <button onClick={() => handleCompleteRecapItem(item, recap.id, i)}
                          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20 transition-colors"
                          data-testid={`complete-recap-${i}`}>
                          <Check size={12} weight="bold" /> Complete
                        </button>
                        <button onClick={() => handleCreateAction(item, recap.id)}
                          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20 transition-colors"
                          data-testid={`action-recap-${i}`}>
                          <Plus size={12} weight="bold" /> Track
                        </button>
                        <button onClick={() => handleDismissRecapItem(recap.id, i)}
                          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold text-muted-foreground hover:bg-muted transition-colors ml-auto"
                          data-testid={`dismiss-recap-${i}`}>
                          <X size={12} /> Dismiss
                        </button>
                      </div>
                      {isAssigning && (
                        <div className="flex items-center gap-2 px-3 pb-2.5 bg-blue-50/50 dark:bg-blue-900/10 rounded-b-md">
                          <Select value={assignTo} onValueChange={setAssignTo}>
                            <SelectTrigger className="h-7 text-[11px] border-blue-200 dark:border-blue-800 w-[160px]"><SelectValue placeholder="Select member" /></SelectTrigger>
                            <SelectContent>{members.map(m => <SelectItem key={m.initials} value={m.initials}>{m.initials} - {m.name}</SelectItem>)}</SelectContent>
                          </Select>
                          <Button size="sm" onClick={() => handleAssignRecapItem(item, recap.id, i)}
                            className="h-7 text-[10px] bg-blue-600 hover:bg-blue-700 text-white">Assign & Track</Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* General Action Items */}
      {recap.general_action_items?.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">General Action Items</h4>
          <div className="space-y-1">
            {recap.general_action_items.map((a, i) => (
              <div key={`action-${recap.id}-${i}`} className="flex items-center justify-between p-2 rounded bg-muted/30 border border-border">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                  <span className="text-xs text-foreground">{a.action}</span>
                  {a.assigned_to && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{a.assigned_to}</span>}
                </div>
                <span className="text-[10px] text-muted-foreground">{a.deadline}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Decisions */}
      {recap.key_decisions?.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Key Decisions</h4>
          <ul className="space-y-1">
            {recap.key_decisions.map((d, i) => (
              <li key={`decision-${recap.id}-${i}`} className="text-xs text-foreground flex items-start gap-2 pl-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />{d}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Follow Ups */}
      {recap.follow_ups_needed?.length > 0 && (
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Follow Ups Needed</h4>
          <ul className="space-y-1">
            {recap.follow_ups_needed.map((f, i) => (
              <li key={`followup-${recap.id}-${i}`} className="text-xs text-foreground flex items-start gap-2 pl-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />{f}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="post-call-recap-view">
      <PageHeader
        title="Today's Huddle Recap"
        subtitle="Automatically built from your Huddle Mode decisions. Add notes or a transcript to enrich it."
        actions={
          <Button variant="outline" onClick={() => setShowManualInput(!showManualInput)}
            className="h-9 border-border text-sm" data-testid="toggle-manual-input">
            <NotePencil size={14} className="mr-1.5" /> {showManualInput ? 'Hide' : 'Add Notes or Transcript'}
          </Button>
        }
      />

      {/* Auto-Generated Huddle Recap */}
      {huddleStats ? (
        <div className="space-y-4" data-testid="auto-huddle-recap">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatCard icon={ListChecks} tone="ai" label="Decisions Made" value={huddleStats.total} testId="stat-decisions" />
            <StatCard icon={Users} tone="info" label="Tasks Assigned" value={huddleStats.assigned} testId="stat-assigned" />
            <StatCard icon={CheckCircle} tone="good" label="Issues Resolved" value={huddleStats.resolved} testId="stat-resolved" />
            <StatCard icon={Clock} tone="high" label="Escalations" value={huddleStats.escalations} testId="stat-escalations" />
            <StatCard icon={PlayCircle} tone="neutral" label="Carried Forward" value={huddleStats.carry} testId="stat-carry" />
          </div>

          <Card className="bg-card border-border">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Sparkle size={16} weight="fill" className="text-violet-400" />
                <h3 className="text-sm font-bold text-foreground">Top Decisions</h3>
                <StatusChip tone="ai">AI PREPARED</StatusChip>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                {huddleSession?.ended_at && <span>Ended {new Date(huddleSession.ended_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                <button onClick={clearHuddleSession} className="text-muted-foreground hover:text-red-500" title="Clear session" data-testid="clear-huddle-session-btn">
                  <X size={14} />
                </button>
              </div>
            </div>
            <div className="divide-y divide-border/50 max-h-[420px] overflow-y-auto">
              {huddleStats.decisions.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">No decisions recorded.</p>
              ) : huddleStats.decisions.map((d, i) => {
                const cfg = OUTCOMES.find(o => o.key === d.outcome) || OUTCOMES[3];
                return (
                  <div key={i} className="flex items-start gap-3 px-4 py-2.5 hover:bg-muted/20">
                    <span className={`w-1.5 h-8 rounded-full ${TONE[cfg.tone]?.dot || TONE.neutral.dot} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-foreground">{d.borrower}</span>
                        <StatusChip tone={cfg.tone}>{cfg.label}</StatusChip>
                        {d.assign_to && <span className="text-[10px] font-bold text-foreground bg-muted px-1.5 py-0.5 rounded">{d.assign_to}</span>}
                        {d.deadline && <span className="text-[10px] text-muted-foreground">· {d.deadline}</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{d.issue}</p>
                      {d.note && <p className="text-xs text-foreground/80 italic mt-0.5">&ldquo;{d.note}&rdquo;</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {huddleStats.carry > 0 && (
            <Card className="bg-card border-border">
              <div className="px-4 py-3 border-b border-border">
                <h3 className="text-sm font-bold text-foreground">Carry Forward to Tomorrow</h3>
              </div>
              <div className="divide-y divide-border/50">
                {huddleStats.decisions.filter(d => d.outcome === 'carry').map((d, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                    <span className={`w-1.5 h-6 rounded-full ${TONE.high.dot}`} />
                    <span className="text-sm font-semibold text-foreground">{d.borrower}</span>
                    <span className="text-xs text-muted-foreground flex-1 truncate">{d.issue}</span>
                    {d.assign_to && <StatusChip tone="neutral">{d.assign_to}</StatusChip>}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      ) : (
        <Card className="bg-card border-dashed border-border">
          <CardContent className="p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-violet-500/15 border border-violet-500/30 mx-auto flex items-center justify-center mb-3">
              <Sparkle size={22} weight="fill" className="text-violet-400" />
            </div>
            <h3 className="text-base font-bold text-foreground">No huddle recorded yet</h3>
            <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">Start a Huddle from Command Center — the recap will auto-build itself as your team works through the queue. You can still paste transcripts or notes below.</p>
          </CardContent>
        </Card>
      )}

      {/* Manual transcript (fallback) */}
      {(showManualInput || !huddleStats) && (
      <Card className="border border-border bg-card" data-testid="recap-input-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <NotePencil size={16} className="text-muted-foreground" />
            Add Notes or Transcript
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Optional. Paste meeting notes or a transcript and Clara will enrich the recap.
            {todayRecap && <span className="text-amber-500 font-medium ml-1">Today&apos;s recap exists — new notes will be appended.</span>}
          </p>
        </CardHeader>
        <CardContent>
          <Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)}
            placeholder={"Paste meeting notes or transcript here..."}
            className="border-border min-h-[120px] mb-3 text-sm" data-testid="recap-transcript-input" />
          <div className="flex items-center justify-between">
            <Button onClick={handleProcess} disabled={processing || !transcript.trim()} className="bg-primary text-primary-foreground hover:opacity-90" data-testid="process-recap-btn">
              {processing ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />Processing...</>
              : <><Sparkle size={16} weight="fill" className="mr-1.5" />{todayRecap ? 'Update Today\'s Recap' : 'Generate Recap'}</>}
            </Button>
            {transcript.trim() && (
              <span className="text-[10px] text-muted-foreground">{transcript.trim().split(/\s+/).length} words</span>
            )}
          </div>
        </CardContent>
      </Card>)}

      {/* Current/Latest Recap */}
      {(currentRecap || todayRecap) && (
        <Card className="border-2 border-amber-500/30 bg-card animate-fade-up" data-testid="current-recap">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                Today&apos;s Recap — {(currentRecap || todayRecap).date}
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 font-medium">Saved</span>
              </CardTitle>
              <a href={exportRecap((currentRecap || todayRecap).id)} target="_blank" rel="noreferrer" data-testid="export-recap-btn">
                <Button variant="outline" size="sm" className="text-xs border-border"><Download size={14} className="mr-1" />Export</Button>
              </a>
            </div>
          </CardHeader>
          <CardContent>{renderRecapContent(currentRecap || todayRecap)}</CardContent>
        </Card>
      )}

      {/* Past Recaps */}
      {pastRecaps.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Past Recaps</h3>
          <div className="space-y-2">
            {pastRecaps.map(recap => (
              <Card key={recap.id} className="border border-border bg-card" data-testid={`past-recap-${recap.id}`}>
                <button onClick={() => setExpandedRecap(expandedRecap === recap.id ? null : recap.id)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-foreground">{recap.date}</span>
                    <span className="text-xs text-muted-foreground truncate max-w-[220px] sm:max-w-[400px]">{recap.recap_summary}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <a href={exportRecap(recap.id)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                      <Download size={14} className="text-muted-foreground hover:text-foreground" />
                    </a>
                    {expandedRecap === recap.id ? <CaretUp size={16} className="text-muted-foreground" /> : <CaretDown size={16} className="text-muted-foreground" />}
                  </div>
                </button>
                {expandedRecap === recap.id && (
                  <CardContent className="pt-0 pb-4">{renderRecapContent(recap)}</CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
